// =============================================================================
// bubbles/mediaAvailability.ts — Quando a mídia deixa de ser "Recebendo…" e
// vira "Mídia indisponível" (F58-S14, CONV-04).
//
// Dois caminhos levam à indisponibilidade, e os dois eram invisíveis:
//   1. A mensagem chegou sem `mediaUrl` e a URL nunca vem (arquivo que nunca
//      foi guardado — `media_storage_key` nula): o skeleton ficava para sempre.
//   2. A URL assinada venceu (ou o arquivo sumiu): o <img>/<video>/<audio>
//      quebra. Pedimos UMA vez uma URL nova (invalida a lista de mensagens, que
//      é quem reassina) e, se ainda assim falhar, desistimos com mensagem clara.
//
// `useConversationSocket` (message:media_ready) continua sendo quem entrega a
// URL quando o download assíncrono termina — se ela chegar depois do prazo, a
// bolha volta a renderizar a mídia normalmente (a fase é derivada, não
// travada).
// =============================================================================

import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

import { conversationKeys } from '../../../queries';

/** Tempo máximo esperando `mediaUrl` antes de declarar a mídia indisponível. */
export const MEDIA_WAIT_TIMEOUT_MS = 15_000;

export type MediaPhase = 'ready' | 'loading' | 'unavailable';

export interface MediaPhaseInput {
  readonly mediaUrl: string | null;
  /** O prazo de espera por `mediaUrl` estourou. */
  readonly waitTimedOut: boolean;
  /** Última URL que falhou ao carregar mesmo depois do refetch (ou `null`). */
  readonly failedUrl: string | null;
}

/** Função pura: decide a fase da bolha a partir do que se sabe. */
export function resolveMediaPhase({
  mediaUrl,
  waitTimedOut,
  failedUrl,
}: MediaPhaseInput): MediaPhase {
  if (!mediaUrl) return waitTimedOut ? 'unavailable' : 'loading';
  // Só a URL que falhou conta: se o refetch trouxe outra, ela merece a chance.
  return failedUrl === mediaUrl ? 'unavailable' : 'ready';
}

/**
 * Agenda o fim do prazo de espera por `mediaUrl`. Devolve o cancelamento —
 * chamado quando a URL chega (ou a bolha desmonta), o aviso NUNCA dispara.
 */
export function scheduleMediaWaitTimeout(
  onTimeout: () => void,
  delayMs: number = MEDIA_WAIT_TIMEOUT_MS,
): () => void {
  const timer = setTimeout(onTimeout, delayMs);
  return () => clearTimeout(timer);
}

export interface MediaRetryDeps {
  /** Pede URL nova (invalida a lista de mensagens, que reassina). */
  readonly refetch: () => Promise<void>;
  /** URL que a bolha tem AGORA (depois do refetch pode ter mudado). */
  readonly getCurrentUrl: () => string | null;
  /** Desistiu: a URL `brokenUrl` não carrega e não há outra chance. */
  readonly onGiveUp: (brokenUrl: string) => void;
}

/**
 * Política "uma nova URL assinada, no máximo": a 1ª falha pede URL nova; se a
 * URL não mudou depois do refetch (ou ele falhou) desiste; qualquer falha
 * seguinte desiste direto, sem novo pedido. Sem React — testável a seco.
 */
export function createMediaRetryController({
  refetch,
  getCurrentUrl,
  onGiveUp,
}: MediaRetryDeps): (brokenUrl: string) => void {
  let retried = false;
  return (brokenUrl) => {
    if (retried) {
      onGiveUp(brokenUrl);
      return;
    }
    retried = true;
    void refetch().then(
      () => {
        if (getCurrentUrl() === brokenUrl) onGiveUp(brokenUrl);
      },
      () => onGiveUp(brokenUrl),
    );
  };
}

export interface MediaAvailability {
  readonly phase: MediaPhase;
  /** Para `onError` do <img>/<video>/<audio>. */
  readonly onMediaError: () => void;
}

export function useMediaAvailability(
  conversationId: string,
  mediaUrl: string | null,
): MediaAvailability {
  const queryClient = useQueryClient();
  const [waitTimedOut, setWaitTimedOut] = React.useState(false);
  const [failedUrl, setFailedUrl] = React.useState<string | null>(null);
  const urlRef = React.useRef(mediaUrl);
  urlRef.current = mediaUrl;

  const hasUrl = Boolean(mediaUrl);

  // Prazo de espera: só corre enquanto não há URL; se ela chega, o cleanup do
  // efeito cancela o timer e o estado volta a zero.
  React.useEffect(() => {
    if (hasUrl) {
      setWaitTimedOut(false);
      return;
    }
    return scheduleMediaWaitTimeout(() => setWaitTimedOut(true));
  }, [hasUrl]);

  const retry = React.useMemo(
    () =>
      createMediaRetryController({
        refetch: () =>
          queryClient.invalidateQueries({ queryKey: conversationKeys.messages(conversationId) }),
        getCurrentUrl: () => urlRef.current,
        onGiveUp: setFailedUrl,
      }),
    [queryClient, conversationId],
  );

  const onMediaError = React.useCallback(() => {
    const brokenUrl = urlRef.current;
    if (brokenUrl) retry(brokenUrl);
  }, [retry]);

  return {
    phase: resolveMediaPhase({ mediaUrl, waitTimedOut, failedUrl }),
    onMediaError,
  };
}
